import type { CSSProperties } from 'react';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { getImageUrl } from '@/utils/images';
import { getTailwindColor } from '@/utils/colors';

type LogoItem = {
    id?: number | null;
    image: {
        __isimage: boolean;
        url: string;
        width?: number | null;
        height?: number | null;
    } | null;
    alt?: string | null;
    link?: {
        url: string;
        title?: string;
        /** Set by the link picker's "Open in new tab" control. */
        target?: string | null;
        rel?: string | null;
    } | null;
    scale?: number | null;
};

/**
 * Every length here is a multiple of two custom properties set outside this file:
 * --logo-box, the reference box, which differs per breakpoint (style/defaults.css),
 * and --logo-size, the per-block size setting written onto the wrapper below.
 */
const scaled = (factor: number) => `calc(var(--logo-box) * var(--logo-size, 1) * ${factor.toFixed(4)})`;

const MIN_SCALE = 25;
const MAX_SCALE = 300;

/**
 * How hard to correct for aspect ratio, 0..1. Every logo is sized as
 * `box * (ar / AR_REFERENCE) ** (1 - AR_CORRECTION)` wide, height following
 * from its own ratio, so this one number moves the whole wall between:
 *
 *   0   — equal height. Widths vary with the ratio, so a wide mark reads as
 *         several times bigger than a square one.
 *   0.5 — equal area. Every logo covers the same ground regardless of shape,
 *         while widths still vary, so rows keep a ragged, packed edge.
 *   1   — equal width for anything landscape, i.e. fitting a square box. Evens
 *         the marks out the most but flattens every row into fixed columns.
 *
 * A logo at AR_REFERENCE is exactly one box wide whatever the correction, so
 * retuning this doesn't resize the wall as a whole.
 */
const AR_CORRECTION = 0.5;
const AR_REFERENCE = 3;

// Guards a banner- or sliver-shaped logo from blowing a row apart.
const MIN_AR = 0.5;
const MAX_AR = 8;

/**
 * Overall scale of the wall, exposed to CSS as --logo-size. Everything derived
 * from the box — logo sizes, and the uniform mobile row height in
 * style/defaults.css — multiplies by it, so one setting keeps the logos and the
 * space around them in proportion.
 */
type LogoSize = 'small' | 'medium' | 'large' | 'xl';

const LOGO_SIZES: Record<LogoSize, number> = {
    small: 0.55,
    medium: 0.75,
    large: 1,
    xl: 1.35,
};

const DEFAULT_LOGO_SIZE: LogoSize = 'large';

/**
 * Tallest a logo can be, as a multiple of the box: the height of a square mark.
 * The mobile grid pins every row to this so the rows come out equal (see
 * [data-logo-wall] in style/defaults.css). Derived rather than written as a
 * literal so it cannot drift out of step with the two constants above — a taller
 * mark than this just grows its row, it is never clipped.
 */
const ROW_MIN_FACTOR = Math.pow(1 / AR_REFERENCE, 1 - AR_CORRECTION);

/**
 * Split the logos into visual rows. 'column' fixes how many go in each row and
 * lets the row count follow; 'row' (the legacy behaviour, and what an absent
 * `layoutType` must mean) fixes the row count and spreads the logos over it.
 *
 * Mirrors chunkLogos in the editor's logo-sizing.js.
 */
const chunkLogos = (logos: LogoItem[], layoutType: 'row' | 'column', rows: number, columns: number) => {
    const perRow =
        layoutType === 'column'
            ? Math.min(Math.max(1, Math.floor(columns || 1)), logos.length)
            : Math.ceil(logos.length / Math.min(Math.max(1, Math.floor(rows || 1)), logos.length));

    return Array.from({ length: Math.ceil(logos.length / perRow) }, (_, row) => logos.slice(row * perRow, (row + 1) * perRow));
};

// `min()` on the width keeps the narrow-viewport shrink the nowrap rows rely on.
const sizeFor = (logo: LogoItem) => {
    const pct = Math.min(MAX_SCALE, Math.max(MIN_SCALE, Math.round(logo.scale || 100))) / 100;
    const w = logo.image?.width || 0;
    const h = logo.image?.height || 0;

    // No usable intrinsic size means no ratio to correct for, so fall back to a
    // plain square box.
    if (w <= 0 || h <= 0) {
        return { maxWidth: `min(100%, ${scaled(pct)})`, maxHeight: scaled(pct) };
    }

    const ar = Math.min(MAX_AR, Math.max(MIN_AR, w / h));
    const factor = Math.pow(ar / AR_REFERENCE, 1 - AR_CORRECTION) * pct;

    return {
        maxWidth: `min(100%, ${scaled(factor)})`,
        maxHeight: scaled(factor / ar),
    };
};

export type LogoGridProps = {
    heading?: string | null;
    logos?: LogoItem[];
    grayscale?: boolean;
    /**
     * Whether the grid is defined by a fixed number of rows or a fixed number of
     * logos per row. Blocks saved before this setting existed have no value, and
     * must keep behaving as 'row'.
     */
    layoutType?: 'row' | 'column';
    rows?: number;
    columns?: number;
    /** Absent on blocks saved before the setting existed -> 'large'. */
    logoSize?: LogoSize | null;
    backgroundColor?: string | null;
};

export const LogoGrid = ({
    heading,
    logos = [],
    grayscale = false,
    layoutType = 'row',
    rows = 1,
    columns = 5,
    logoSize,
    backgroundColor,
}: LogoGridProps) => {
    const items = logos.filter(logo => logo.image?.url);

    if (items.length === 0) {
        return null;
    }

    const chunks = chunkLogos(items, layoutType, rows, columns);

    const sizeMultiplier = LOGO_SIZES[logoSize ?? DEFAULT_LOGO_SIZE] ?? LOGO_SIZES[DEFAULT_LOGO_SIZE];

    const bgClass = getTailwindColor(backgroundColor, 'bg');

    const renderLogo = (logo: LogoItem) => {
        if (!logo.image?.url) {
            return null;
        }

        const img = (
            <img
                src={getImageUrl(logo.image.url, '300xAUTO')}
                alt={logo.alt ?? ''}
                width={logo.image.width ?? undefined}
                height={logo.image.height ?? undefined}
                loading="lazy"
                style={sizeFor(logo)}
                className={clsx('r-h-auto r-w-auto r-object-contain', {
                    'r-grayscale r-transition-[filter] r-duration-200 hover:r-grayscale-0': grayscale,
                })}
            />
        );

        if (!logo.link?.url) {
            return img;
        }

        const opensInNewTab = logo.link.target === '_blank';
        // The image alt already names the link; only fall back to an aria-label
        // when there is no alt, since it would otherwise also swallow the
        // "external link, opens in new tab" hint that page.tsx appends.
        const ariaLabel = logo.alt ? undefined : logo.link.title || undefined;

        return (
            <Link
                to={logo.link.url}
                // `no-icon` opts out of the external-link glyph page.tsx bolts
                // onto outbound links — next to a bare logo it reads as clutter.
                className="no-icon r-inline-flex r-max-w-full"
                {...(opensInNewTab && { target: '_blank', rel: logo.link.rel || 'noreferrer noopener' })}
                {...(ariaLabel && { 'aria-label': ariaLabel })}
            >
                {img}
            </Link>
        );
    };

    return (
        <div
            data-component="logo-grid"
            // --logo-box is defined per breakpoint in style/defaults.css.
            style={
                {
                    '--logo-size': sizeMultiplier,
                    '--logo-row-min': scaled(ROW_MIN_FACTOR),
                } as CSSProperties
            }
            className={clsx('r-w-full r-px-6 md:r-px-10', bgClass, {
                'r-py-10': !!bgClass,
            })}
        >
            {heading ? (
                <h2 className="r-mb-8 r-text-center r-text-2xl r-font-semibold md:r-text-3xl" dangerouslySetInnerHTML={{ __html: heading }} />
            ) : null}
            {/*
                Mobile: outer is a 2-column grid, chunk wrappers use `display: contents`
                so all logos flow into the grid as one flat set — they align into two
                even columns, each logo centered within its cell.
                Desktop (md+): outer becomes a vertical stack, each chunk becomes a
                no-wrap row with proportional shrink.
            */}
            <div
                data-logo-wall
                className="r-grid r-grid-cols-2 r-items-center r-justify-items-center r-gap-x-8 r-gap-y-2 md:r-flex md:r-flex-col md:r-items-stretch md:r-justify-items-stretch md:r-gap-y-8"
            >
                {chunks.map((chunk, row) => (
                    <div key={row} className="r-contents md:r-flex md:r-flex-nowrap md:r-items-center md:r-justify-center md:r-gap-x-8 lg:r-gap-x-10">
                        {chunk.map((logo, i) => (
                            <div key={logo.id ?? i} className="r-flex r-items-center r-justify-center md:r-min-w-0">
                                {renderLogo(logo)}
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
};
